import { PropertyUpdateViewModel, PropertyViewModel } from "@common/properties/property.model";
import {
  Button,
  Code,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef, useCallback } from "react";
import LocationAutocomplete from "components/elements/LocationAutocomplete";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";
import { propertyUpdateSchema } from "@common/properties/property.schema";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { api } from "utils/api";
import { useMutation } from "@tanstack/react-query";
import { BorrowerCompanySelect } from "components/loans/CreateNewLoanApplication/components/BorrowerCompanySelect";
import { EntitySelect } from "components/loans/CreateNewLoanApplication/components/EntitySelect";

interface PropertyEditButtonProps {
  property: PropertyViewModel;
  onUpdate: () => void;
}

export function PropertyEditButton({ property, onUpdate }: PropertyEditButtonProps) {
  const btnRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { mutate: updateProperty, isPending: isUpdating } = useMutation({
    mutationFn: async (viewModel: PropertyUpdateViewModel) => {
      await api.put(`/properties/${property.id}`, viewModel);
    },
    onSuccess: () => {
      onUpdate();
      onClose();
    },
  });

  const form = useForm<PropertyUpdateViewModel>({
    resolver: zodResolver(propertyUpdateSchema),
    defaultValues: {
      name: property.name,
      company: property.company.id,
      entity: property.entity?.id,
      purchase_date: property.purchase_date,
      completion_date: property.completion_date,
      address: property.address,
    },
  });

  const companyId = form.watch("company");
  const entityId = form.watch("entity");

  const handleCompanyChange = useCallback(
    (value: string | undefined) => {
      form.setValue("company", value ?? "", { shouldValidate: true });
      form.setValue("entity", null, { shouldValidate: true });
    },
    [form]
  );

  const handleEntityChange = useCallback(
    (value: string | undefined) => {
      form.setValue("entity", value ?? "", { shouldValidate: true });
    },
    [form]
  );

  const onSubmit = (viewModel: PropertyUpdateViewModel) => {
    updateProperty(viewModel);
  };

  const onError = (errors: FieldErrors<PropertyUpdateViewModel>) => {
    console.log("Validation errors", errors);
  };

  return (
    <>
      <Button variant="link" size="sm" color="primary" onClick={onOpen}>
        Edit Property
      </Button>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} finalFocusRef={btnRef} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Edit Details</DrawerHeader>
          <DrawerBody
            as="form"
            onSubmit={form.handleSubmit(onSubmit, onError)}
            noValidate
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            gap={4}
          >
            <Flex direction="column" gap={4}>
              <Input {...form.register("company")} type="hidden" />
              <FormControl isInvalid={!!form.formState.errors.name} isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  variant="primary"
                  type="text"
                  {...form.register("name")}
                  placeholder="Name"
                />
                <FormErrorMessage>{form.formState.errors.name?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Borrower Company</FormLabel>
                <BorrowerCompanySelect
                  borrowerCompanyId={companyId}
                  setBorrowerCompanyId={handleCompanyChange}
                />
              </FormControl>
              {companyId && companyId !== "" && (
                <FormControl>
                  <FormLabel>Entity</FormLabel>
                  <EntitySelect
                    entityId={entityId ?? undefined}
                    setEntityId={handleEntityChange}
                    companyId={companyId}
                  />
                </FormControl>
              )}

              <FormControl isInvalid={!!form.formState.errors.purchase_date}>
                <FormLabel>Purchase Date</FormLabel>
                <Input
                  variant="primary"
                  type="date"
                  {...form.register("purchase_date")}
                  placeholder="Purchase Date"
                />
                <FormErrorMessage>{form.formState.errors.purchase_date?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!form.formState.errors.completion_date}>
                <FormLabel>Completion Date</FormLabel>
                <Input
                  variant="primary"
                  type="date"
                  {...form.register("completion_date")}
                  placeholder="Completion Date"
                />
                <FormErrorMessage>
                  {form.formState.errors.completion_date?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!form.formState.errors.address}>
                <FormLabel>Address</FormLabel>
                <LocationAutocomplete
                  onChange={val => form.setValue("address", val.formatted)}
                  defaultValue={form.watch("address")}
                  placeholder="Address"
                />
                <FormErrorMessage>{form.formState.errors.address?.message}</FormErrorMessage>
              </FormControl>
            </Flex>
            <Flex gap={2}>
              <Button variant="outline" onClick={onClose} flex={1}>
                Cancel
              </Button>
              <Button
                type="submit"
                flex={1}
                textTransform="capitalize"
                isLoading={isUpdating}
                leftIcon={<FontAwesomeIcon icon={["far", "save"]} />}
              >
                Save Changes
              </Button>
            </Flex>
          </DrawerBody>
          <DrawerFooter></DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
